import { useSecurityStore } from "@/dod/store/dsecurity"

// only the sound effects directly in assets/sounds; a template require() would bundle every file below it
const soundFiles = require.context("@/assets/sounds", false, /\.mp3$/);

// Track if user has interacted with the page
let userInteracted = false;

// iOS only lets an <audio> element play without a tap once it has been started inside a tap. The sounds that come
// with a server update instead of the player's own tap (your turn, game over, winner) therefore get one element per
// sound, started muted on a tap and kept; the others are new elements played inside the tap that causes them.
const UNLOCK_KEYS = ["NOTIFY", "GAME_OVER", "WIN"];
const unlockedAudio = {}; // sound key -> an element that may play without a tap (any of the key's files)
let unlocking = false;

const INTERACTION_EVENTS = ["click", "touchend", "keydown"]; // iOS counts touchend, not touchstart, as a tap for audio

// main.js loads this file before Pinia exists (to hear the first taps), so the store is looked up when needed
function soundSettingOn() {
    const setting = useSecurityStore().user?.Settings?.SOUND;
    return setting != null &&
        (setting === "1" ||
            setting === "true" ||
            setting === "True" ||
            setting === "on" ||
            setting === "On" ||
            setting === "yes");
}

function keysToUnlock() {
    return UNLOCK_KEYS.filter(key => !unlockedAudio[key]);
}

function removeInteractionListeners() {
    INTERACTION_EVENTS.forEach(name => document.removeEventListener(name, markUserInteraction, true));
}

// runs inside the tap: play() has to be called right here, the rest may follow later
function unlockAudio() {
    if (unlocking || !soundSettingOn())
        return; // with the sound off nothing is downloaded; the tap that turns it on comes back here
    var keys = keysToUnlock();
    if (keys.length === 0) {
        removeInteractionListeners();
        return;
    }
    unlocking = true;
    Promise.all(keys.map(key => {
        var audio = new Audio(sounds._url(sounds._keys[key][0]));
        audio.muted = true;
        var keep = () => {
            audio.pause();
            audio.currentTime = 0;
            audio.muted = false;
            unlockedAudio[key] = audio;
        };
        var played;
        try {
            played = audio.play();
        }
        catch (error) {
            return null;
        }
        return Promise.resolve(played).then(keep).catch(error => {
            // interrupted by another one starting (older iOS plays one at a time) still counts as started in the tap;
            // not allowed: the next tap tries again
            if (error && error.name === "AbortError")
                keep();
        });
    })).finally(() => {
        unlocking = false;
        if (keysToUnlock().length === 0)
            removeInteractionListeners();
    });
}

function markUserInteraction() {
    userInteracted = true;
    unlockAudio();
}

INTERACTION_EVENTS.forEach(name => document.addEventListener(name, markUserInteraction, true));

const sounds = {
    // sound files are in the assets folder
    KEYS: {
        PLACE_CARD: "PLACE_CARD",
        SHUFFLE: "SHUFFLE",
        NOTIFY: "NOTIFY",
        GAME_OVER: "GAME_OVER",
        ERROR: "ERROR",
        WIN: "WIN",
        CHAT: "CHAT",
        HEART: "HEART",
    },

    _keys: {
        PLACE_CARD: ["card_1.mp3"],
        SHUFFLE: ["shuffle_1.mp3"],
        NOTIFY: ["notify_1.mp3", "notify_2.mp3"],
        GAME_OVER: ["game_over_1.mp3", "game_over_2.mp3"],
        ERROR: ["error_1.mp3"],
        WIN: ["winner_1.mp3", "winner_2.mp3", "winner_3.mp3"],
        CHAT: ["chat_1.mp3"],
        HEART: ["heart_1.mp3"]
    },

    // special case when empty card
    PlaySound: function (sound_key) {
        try {
            // check user interaction and sound setting
            if (!this.IsSoundEnabled()) {
                console.log("Sound not played: either disabled in settings or user hasn't interacted.");
                return;
            }

            // invalid sound key
            if (!this._keys[sound_key])
                return;

            // get the sound files for the key
            var files = this._keys[sound_key];
            // get random file from the array of files
            var file = this._getFile(files);
            var url = this._url(file);
            // the unlocked element when there is one (it plays without a tap on iOS), otherwise a new one
            var audio = unlockedAudio[sound_key];
            if (audio) {
                if (audio.getAttribute("src") !== url)
                    audio.src = url;
                else
                    audio.currentTime = 0;
            }
            else
                audio = new Audio(url);
            audio.play().catch(error => {
                console.error("Error playing sound: ", error);
            });
        }
        catch (error) {
            console.error("Error playing sound: ", error);
        }
    },

    _url(file) {
        return soundFiles("./" + file);
    },

    IsSoundEnabled: function () {
        return soundSettingOn() && userInteracted;
    },

    _getFile: function (files) {
        var length = files.length;
        if (length === 1)
            return files[0];
        return files[Math.floor(Math.random() * length)];
    }
}

export default sounds;
